import { useState, useEffect } from "react";
import { queryKeys } from "@/lib/queryKeys";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Loader2 } from "lucide-react";
import type { AdminUser } from "../types";
import { useApiMutation } from "@/hooks/useApiMutation";
import { showError } from "@/lib/toastHelpers";


interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser | null;
}

export function EditUserDialog({ open, onOpenChange, user }: EditUserDialogProps) {
  
  const [formData, setFormData] = useState({
    email: user?.email || '',
    confirmEmail: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    username: user?.username || '',
    password: '',
    confirmPassword: '',
    role: user?.role || 'user',
    isActive: user?.isActive ?? true
  });

  const [errors, setErrors] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    confirmEmail: ''
  });

  const updateUserMutation = useApiMutation({
    endpoint: (vars: { userId?: string }) => `/api/admin/users/${vars.userId}?t=${Date.now()}`,
    method: "PATCH",
    invalidate: [queryKeys.admin.users()],
    successMessage: "User updated successfully",
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (errors.username || errors.password || errors.confirmPassword || errors.email || errors.confirmEmail) {
      showError("Please fix the validation errors before submitting", "Validation Error");
      return;
    }
    
    updateUserMutation.mutate({ userId: user?.id, ...formData });
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate on change
    if (field === 'username') {
      const username = value as string;
      if (username.length > 0 && username.length < 3) {
        setErrors(prev => ({ ...prev, username: 'Username must be at least 3 characters' }));
      } else if (username.length > 20) {
        setErrors(prev => ({ ...prev, username: 'Username must be 20 characters or less' }));
      } else {
        setErrors(prev => ({ ...prev, username: '' }));
      }
    }
    
    if (field === 'password') {
      const password = value as string;
      if (password.length > 0 && password.length < 6) {
        setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters' }));
      } else if (password.length > 50) {
        setErrors(prev => ({ ...prev, password: 'Password must be 50 characters or less' }));
      } else {
        setErrors(prev => ({ ...prev, password: '' }));
      }
      
      // Check confirm password match
      if (formData.confirmPassword && password !== formData.confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
    
    if (field === 'confirmPassword') {
      const confirmPassword = value as string;
      if (confirmPassword && confirmPassword !== formData.password) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
    
    if (field === 'email') {
      const email = value as string;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (email && !emailRegex.test(email)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
      
      // Check confirm email match
      if (formData.confirmEmail && email !== formData.confirmEmail) {
        setErrors(prev => ({ ...prev, confirmEmail: 'Email addresses do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmEmail: '' }));
      }
    }
    
    if (field === 'confirmEmail') {
      const confirmEmail = value as string;
      if (confirmEmail && confirmEmail !== formData.email) {
        setErrors(prev => ({ ...prev, confirmEmail: 'Email addresses do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmEmail: '' }));
      }
    }
  };

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        confirmEmail: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        password: '',
        confirmPassword: '',
        role: user.role || 'user',
        isActive: user.isActive ?? true
      });
      setErrors({ username: '', password: '', confirmPassword: '', email: '', confirmEmail: '' });
    }
  }, [user]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update user information and permissions</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="First name"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              placeholder="Username (3-20 characters)"
              className={errors.username ? "border-red-500" : ""}
            />
            {errors.username && (
              <p className="text-sm text-red-500 mt-1">{errors.username}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="New password (6-50 characters, leave empty to keep current)"
              className={errors.password ? "border-red-500" : ""}
            />
            {errors.password && (
              <p className="text-sm text-red-500 mt-1">{errors.password}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              placeholder="Confirm new password"
              className={errors.confirmPassword ? "border-red-500" : ""}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.confirmPassword}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Email address"
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="confirmEmail">Confirm Email</Label>
            <Input
              id="confirmEmail"
              type="email"
              value={formData.confirmEmail}
              onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
              placeholder="Confirm email address"
              className={errors.confirmEmail ? "border-red-500" : ""}
            />
            {errors.confirmEmail && (
              <p className="text-sm text-red-500 mt-1">{errors.confirmEmail}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="role">Role</Label>
            <Select value={formData.role} onValueChange={(value) => handleInputChange('role', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => handleInputChange('isActive', checked)}
            />
            <Label htmlFor="isActive">Account Active</Label>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateUserMutation.isPending || !!errors.username || !!errors.password || !!errors.confirmPassword || !!errors.email || !!errors.confirmEmail}>
              {updateUserMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update User
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
